import { z } from "zod";
import { JobStatus, JobType, StemType } from "./enums";
import {
  ResolutionSchema,
  SubtitlesSourceAlignmentSchema,
  SubtitlesSourceTranscriptionSchema,
  VideoSourceSchema,
} from "./create";
import { AudioShakeJobSchema } from "./audioshake";
import { StemModelSchema } from "./stem";
export const RunpodJobStatusSchema = z.enum([
  "IN_QUEUE",
  "RUNNING",
  "COMPLETED",
  "TIMED_OUT",
  "FAILED",
]);

export const StemTypeSchema = z.nativeEnum(StemType);
export const FileTypeSchema = z.enum(["audio", "video"]);

export const JobTypeSchema = z.nativeEnum(JobType);

export const JobInputBaseSchema = z.object({
  type: JobTypeSchema,
  data: z.record(z.any()),
});

export const JobOutputBaseSchema = z.object({
  type: JobTypeSchema,
  data: z.record(z.any()),
});

export const JobStatusSchema = z.enum([
  "pending",
  "running",
  "completed",
  "failed",
]);

export const S3FileSchema = z.object({
  bucket: z.string(),
  key: z.string(),
  size: z.number(),
  mimeType: z.string(),
});

export const JobAudioAndVideoFromVideoInputDataSchema = z.object({
  videoUrl: z.string(),
});

export const JobAudioFromAudioInputDataSchema = z.object({
  audioUrl: z.string(),
});

export const JobAudioFromAudioOutputDataSchema = z.object({
  audio: S3FileSchema,
});

export const JobAlignmentOutputDataSchema = AudioShakeJobSchema.extend({
  audioShakeAlignment: z.any(),
});

export const JobAlignmentOutputSchema = JobOutputBaseSchema.extend({
  type: z.literal(JobType.SyncLyrics),
  data: JobAlignmentOutputDataSchema,
});

export const JobRenderVideoWithAudioStemSchema = z.object({
  type: StemTypeSchema,
  url: z.string().url(),
});

export const JobRenderVideoWithAudioInputDataSchema = z.object({
  videoSource: VideoSourceSchema,
  instrumentalStemUrl: z.string().url(),
  originalStemUrl: z.string().url(),
});

export const JobRenderVideoWithAudioOutputDataSchema = z.object({
  instrumentalVideo: S3FileSchema,
  originalVideo: S3FileSchema,
  thumbnail: S3FileSchema,
  resolution: ResolutionSchema,
  duration: z.number(),
});

export const JobRenderVideoWithAudioInputSchema = JobInputBaseSchema.extend({
  type: z.literal(JobType.RenderVideoWithAudio),
  data: JobRenderVideoWithAudioInputDataSchema,
});

export const JobRenderVideoWithAudioOutputSchema = JobOutputBaseSchema.extend({
  type: z.literal(JobType.RenderVideoWithAudio),
  data: JobRenderVideoWithAudioOutputDataSchema,
});

export const JobGenerateVideoInputDataSchema = VideoSourceSchema;

export const JobGenerateVideoInputSchema = JobInputBaseSchema.extend({
  type: z.literal(JobType.GenerateVideo),
  data: JobGenerateVideoInputDataSchema,
});

export const JobGenerateVideoOutputDataSchema = z.object({
  video: S3FileSchema,
  thumbnail: S3FileSchema,
  resolution: ResolutionSchema,
  duration: z.number(),
});

export const JobGenerateVideoOutputSchema = JobOutputBaseSchema.extend({
  type: z.literal(JobType.GenerateVideo),
  data: JobGenerateVideoOutputDataSchema,
});

export const JobExportProjectInputDataSchema = z.object({
  exportId: z.string(),
  videoUrl: z.string(),
  assUrl: z.string(),
});

export const JobExportProjectInputSchema = JobInputBaseSchema.extend({
  type: z.literal(JobType.ExportProject),
  data: JobExportProjectInputDataSchema,
});

export const JobExportProjectOutputDataSchema = z.object({
  video: S3FileSchema,
  duration: z.number(),
});

export const JobExportProjectOutputSchema = JobOutputBaseSchema.extend({
  type: z.literal(JobType.ExportProject),
  data: JobExportProjectOutputDataSchema,
});

export const JobNormalizeAudioInputDataSchema = z.object({
  url: z.string(),
});

export const JobNormalizeAudioInputSchema = JobInputBaseSchema.extend({
  type: z.literal(JobType.NormalizeAudio),
  data: JobNormalizeAudioInputDataSchema,
});

export const JobNormalizeAudioOutputDataSchema = z.object({
  audio: S3FileSchema,
});

export const JobNormalizeAudioOutputSchema = JobOutputBaseSchema.extend({
  type: z.literal(JobType.NormalizeAudio),
  data: JobNormalizeAudioOutputDataSchema,
});

export const JobSyncLyricsInputDataSchema = z.discriminatedUnion("type", [
  SubtitlesSourceTranscriptionSchema,
  SubtitlesSourceAlignmentSchema,
  // SubtitlesSourceId3Schema,
  // SubtitlesSourceLrcSchema,
  // SubtitlesSourceKarafunSchema,
  // SubtitlesSourceAutoSchema,
]);

export const JobSyncLyricsInputSchema = JobInputBaseSchema.extend({
  type: z.literal(JobType.SyncLyrics),
  data: JobSyncLyricsInputDataSchema,
});

export const JobSyncLyricsOutputDataSchema = z.object({});

export const JobSyncLyricsOutputSchema = JobOutputBaseSchema.extend({
  type: z.literal(JobType.SyncLyrics),
  data: JobSyncLyricsOutputDataSchema,
});

export const JobStemSeparationInputDataSchema = z.object({
  model: StemModelSchema,
  // fileType: FileTypeSchema,
  url: z.string(),
});

export const JobStemSeparationInputSchema = JobInputBaseSchema.extend({
  type: z.literal(JobType.StemSeparation),
  data: JobStemSeparationInputDataSchema,
});

export const RunpodWebhookSchema = z.object({
  id: z.string(),
  status: RunpodJobStatusSchema,
  retries: z.number().optional(),
  delayTime: z.number().optional(),
  executionTime: z.number().optional(),
  gpu: z.string().optional(),
  input: z.record(z.any()).optional(),
  output: z.any().optional(),
  error: z.string().optional(),
});

export const RunpodStemSchema = z.object({
  type: z.enum(["vocals", "instrumental"]),
  bucket: z.string(),
  key: z.string(),
  mimeType: z.string(),
  size: z.number(),
});

export const RunpodSplitOutputSchema = z.object({
  stems: z.array(RunpodStemSchema),
});

export const RunpodSplitWebhookSchema = RunpodWebhookSchema.extend({
  output: z.optional(RunpodSplitOutputSchema),
});

export const JobStemSeparationOutputDataSchema = RunpodSplitWebhookSchema;

export const JobStemSeparationOutputSchema = JobOutputBaseSchema.extend({
  type: z.literal(JobType.StemSeparation),
  data: JobStemSeparationOutputDataSchema,
});

export const JobInputSchema = z.discriminatedUnion("type", [
  JobGenerateVideoInputSchema,
  JobExportProjectInputSchema,
  JobSyncLyricsInputSchema,
  JobStemSeparationInputSchema,
  JobNormalizeAudioInputSchema,
  JobRenderVideoWithAudioInputSchema,
]);

export const JobOutputSchema = z.discriminatedUnion("type", [
  JobGenerateVideoOutputSchema,
  JobExportProjectOutputSchema,
  JobSyncLyricsOutputSchema,
  JobStemSeparationOutputSchema,
  JobNormalizeAudioOutputSchema,
  JobRenderVideoWithAudioOutputSchema,
]);

export type RunpodJobStatus = z.infer<typeof RunpodJobStatusSchema>;

export const RunpodJobTypeSchema = z.enum(["split"]);
export type RunpodJobType = z.infer<typeof RunpodJobTypeSchema>;

const createJobWebhookDataSchema = <OutputSchema extends z.ZodTypeAny>(
  outputSchema: OutputSchema
) => {
  return z.discriminatedUnion("status", [
    z.object({
      status: z.literal(JobStatus.Queued),
    }),
    z.object({
      status: z.literal(JobStatus.InProgress),
    }),
    z.object({
      status: z.literal(JobStatus.Completed),
      output: outputSchema,
    }),
    z.object({
      status: z.literal(JobStatus.Failed),
      error: z.string().optional(),
    }),
  ]);
};

export const JobNormalizeAudioWebhookSchema = createJobWebhookDataSchema(
  JobNormalizeAudioOutputSchema
);

export const JobExportProjectWebhookSchema = createJobWebhookDataSchema(
  JobExportProjectOutputSchema
);

export const JobRenderVideoWithAudioWebhookSchema = createJobWebhookDataSchema(
  JobRenderVideoWithAudioOutputSchema
);

export const JobGenerateVideoWebhookSchema = createJobWebhookDataSchema(
  JobGenerateVideoOutputSchema
);

export type JobExportProjectInput = z.infer<typeof JobExportProjectInputSchema>;
export type JobInput = z.infer<typeof JobInputSchema>;
export type JobExportProjectOutput = z.infer<
  typeof JobExportProjectOutputSchema
>;
export type JobOutput = z.infer<typeof JobOutputSchema>;
export type S3File = z.infer<typeof S3FileSchema>;
export type JobExportProjectInputData = z.infer<
  typeof JobExportProjectInputDataSchema
>;
export type JobExportProjectOutputData = z.infer<
  typeof JobExportProjectOutputDataSchema
>;
export type JobAudioAndVideoFromVideoInputData = z.infer<
  typeof JobAudioAndVideoFromVideoInputDataSchema
>;
export type JobAudioFromAudioInputData = z.infer<
  typeof JobAudioFromAudioInputDataSchema
>;
export type JobAudioFromAudioOutputData = z.infer<
  typeof JobAudioFromAudioOutputDataSchema
>;
export type JobSyncLyricsInputData = z.infer<
  typeof JobSyncLyricsInputDataSchema
>;
export type JobStemSeparationInputData = z.infer<
  typeof JobStemSeparationInputDataSchema
>;
export type JobStemSeparationOutputData = z.infer<
  typeof JobStemSeparationOutputDataSchema
>;
export type JobGenerateVideoInput = z.infer<typeof JobGenerateVideoInputSchema>;
export type JobGenerateVideoOutput = z.infer<
  typeof JobGenerateVideoOutputSchema
>;
export type JobGenerateVideoOutputData = z.infer<
  typeof JobGenerateVideoOutputDataSchema
>;
export type JobSyncLyricsInput = z.infer<typeof JobSyncLyricsInputSchema>;
export type JobSyncLyricsOutput = z.infer<typeof JobSyncLyricsOutputSchema>;
export type JobStemSeparationInput = z.infer<
  typeof JobStemSeparationInputSchema
>;
export type JobStemSeparationOutput = z.infer<
  typeof JobStemSeparationOutputSchema
>;

export type JobGenerateVideoInputData = z.infer<
  typeof JobGenerateVideoInputDataSchema
>;

export type JobNormalizeAudioInputData = z.infer<
  typeof JobNormalizeAudioInputDataSchema
>;
export type JobNormalizeAudioOutputData = z.infer<
  typeof JobNormalizeAudioOutputDataSchema
>;

export type JobNormalizeAudioInput = z.infer<
  typeof JobNormalizeAudioInputSchema
>;
export type JobNormalizeAudioOutput = z.infer<
  typeof JobNormalizeAudioOutputSchema
>;

export type JobRunpodWebhook = z.infer<typeof RunpodWebhookSchema>;
export type JobRunpodSplitWebhook = z.infer<typeof RunpodSplitWebhookSchema>;
export type JobExportProjectWebhook = z.infer<
  typeof JobExportProjectWebhookSchema
>;

export type JobRenderVideoWithAudioWebhook = z.infer<
  typeof JobRenderVideoWithAudioWebhookSchema
>;

export type JobGenerateVideoWebhook = z.infer<
  typeof JobGenerateVideoWebhookSchema
>;

export type JobNormalizeAudioWebhook = z.infer<
  typeof JobNormalizeAudioWebhookSchema
>;

export type JobRenderVideoWithAudioOutputData = z.infer<
  typeof JobRenderVideoWithAudioOutputDataSchema
>;

export type JobRenderVideoWithAudioOutput = z.infer<
  typeof JobRenderVideoWithAudioOutputSchema
>;

export type JobRenderVideoWithAudioInput = z.infer<
  typeof JobRenderVideoWithAudioInputSchema
>;

export type JobRenderVideoWithAudioInputData = z.infer<
  typeof JobRenderVideoWithAudioInputDataSchema
>;
